// src/components/ErrorBoundary.jsx
import React from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { formatErrorForLogging, getUserMessage } from '../lib/errors';
import styles from './ErrorBoundary.module.css';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
    if (this.props.onReset) this.props.onReset();
  };

  handleGoHome = () => { window.location.href = '/'; };

  toggleDetails = () => { this.setState(prev => ({ showDetails: !prev.showDetails })); };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.handleReset);

      const userMessage = getUserMessage(this.state.error);
      const technicalMessage = formatErrorForLogging(this.state.error);

      return (
        <div className={styles.page}>
          <div className={styles.card}>
            <div className={styles.iconWrap}>
              <div className={styles.iconBadge}>
                <AlertCircle size={48} />
              </div>
            </div>
            <h1 className={styles.title}>Oops! Something went wrong</h1>
            <p className={styles.message}>{userMessage}</p>

            <div className={styles.actions}>
              <button onClick={this.handleReset} className={`${styles.btnPrimary} btn-primary`}>
                <RefreshCw size={20} /> Try Again
              </button>
              <button onClick={this.handleGoHome} className={styles.btnSecondary}>
                <Home size={20} /> Go Home
              </button>
            </div>

            {import.meta.env.DEV && (
              <div className={styles.devSection}>
                <button onClick={this.toggleDetails} className={styles.toggleBtn}>
                  {this.state.showDetails ? 'Hide' : 'Show'} technical details
                </button>
                {this.state.showDetails && (
                  <div className={styles.details}>
                    <p className={styles.detailsTitle}>Error Details:</p>
                    <pre className={styles.detailsPre}>{technicalMessage}</pre>
                    {this.state.errorInfo && (
                      <>
                        <p className={styles.detailsTitle}>Component Stack:</p>
                        <pre className={styles.detailsPre}>{this.state.errorInfo.componentStack}</pre>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            <p className={styles.helpText}>
              If this problem persists, please contact support or try again later.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export function useErrorHandler() {
  const [error, setError] = React.useState(null);
  React.useEffect(() => { if (error) throw error; }, [error]);
  return setError;
}

export function InlineErrorFallback({ error, onReset, title = 'Error loading data' }) {
  return (
    <div className={styles.inline}>
      <AlertCircle className={styles.inlineIcon} size={32} />
      <h3 className={styles.inlineTitle}>{title}</h3>
      <p className={styles.inlineMsg}>{error?.message || 'An unexpected error occurred'}</p>
      {onReset && (
        <button onClick={onReset} className={styles.inlineBtn}>
          <RefreshCw size={16} /> Try Again
        </button>
      )}
    </div>
  );
}

export default ErrorBoundary;
